import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DesignDataCell } from '@/components/result/DesignDataCell';
import { CommentToggleButton, ExpandableCommentTableRows } from './CommentExpand';
import { ScenarioResultBadge } from './ScenarioResultBadge';
import { GcsComparedValueCell } from '@/utils/evaluationResultUtils';
import { formatGenderAge, isScenarioAnswerCorrect } from '@/utils/scenarioResultFormat';
import { AssessmentTableShell } from './AssessmentResult';

export function GcsAssessmentTable({ title, rows }: { title: string; rows: any[] }): React.ReactElement | null {
  const { t } = useTranslation('class');
  const { t: tScenario } = useTranslation('scenario');
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  if (!rows?.length) {
    return (
      <AssessmentTableShell title={title}>
        <table className="w-full text-sm text-center" role="presentation">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="px-4 py-3 font-semibold text-gray-700 text-center">{tScenario('gcsEvaluationResult.tableNo')}</th>
              <th className="px-4 py-3 font-semibold text-gray-700 text-start">{tScenario('gcsEvaluationResult.tableName')}</th>
              <th className="px-4 py-3 font-semibold text-gray-700 text-center">
                {tScenario('gcsEvaluationResult.tableGender')}/{tScenario('gcsEvaluationResult.tableAge')}
              </th>
              <th className="px-4 py-3 font-semibold text-gray-700 text-center">{tScenario('gcsEvaluationResult.tableEyeResponse')}</th>
              <th className="px-4 py-3 font-semibold text-gray-700 text-center">{tScenario('gcsEvaluationResult.tableVerbalResponse')}</th>
              <th className="px-4 py-3 font-semibold text-gray-700 text-center">{tScenario('gcsEvaluationResult.tableMotorResponse')}</th>
              <th className="px-4 py-3 font-semibold text-gray-700 text-center">{tScenario('gcsEvaluationResult.tableTotal')}</th>
              <th className="px-4 py-3 font-semibold text-gray-700 text-center">{tScenario('gcsEvaluationResult.tableResult')}</th>
              <th className="px-4 py-3 font-semibold text-gray-700 text-center">{tScenario('gcsEvaluationResult.tableComment')}</th>
            </tr>
          </thead>
          <tbody>
            {[0, 1, 2].map((r) => (
              <tr className="border-b border-slate-100/80" key={`gcs-skel-${r}`}>
                <td className="px-4 py-3 text-center text-slate-500 align-top font-medium">{r + 1}</td>
                <td className="px-4 py-3 align-top"><DesignDataCell hint="이름" align="left" /></td>
                <td className="px-4 py-3 align-top"><DesignDataCell hint="성/연" align="center" /></td>
                <td className="px-4 py-3 align-top"><DesignDataCell hint="눈" align="center" /></td>
                <td className="px-4 py-3 align-top"><DesignDataCell hint="말" align="center" /></td>
                <td className="px-4 py-3 align-top"><DesignDataCell hint="운동" align="center" /></td>
                <td className="px-4 py-3 align-top"><DesignDataCell hint="GCS" align="center" /></td>
                <td className="px-4 py-3 align-top text-center text-slate-500"><DesignDataCell hint="O/X" align="center" /></td>
                <td className="px-4 py-3 align-top text-slate-400 text-center">···</td>
              </tr>
            ))}
          </tbody>
        </table>
      </AssessmentTableShell>
    );
  }

  return (
    <AssessmentTableShell title={title}>
      <table className="w-full text-sm text-center">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-200">
            <th className="px-4 py-3 font-semibold text-gray-700 text-center">{tScenario('gcsEvaluationResult.tableNo')}</th>
            <th className="px-4 py-3 font-semibold text-gray-700 text-start">{tScenario('gcsEvaluationResult.tableName')}</th>
            <th className="px-4 py-3 font-semibold text-gray-700 text-center">
              {tScenario('gcsEvaluationResult.tableGender')}/{tScenario('gcsEvaluationResult.tableAge')}
            </th>
            <th className="px-4 py-3 font-semibold text-gray-700 text-center">{tScenario('gcsEvaluationResult.tableEyeResponse')}</th>
            <th className="px-4 py-3 font-semibold text-gray-700 text-center">{tScenario('gcsEvaluationResult.tableVerbalResponse')}</th>
            <th className="px-4 py-3 font-semibold text-gray-700 text-center">{tScenario('gcsEvaluationResult.tableMotorResponse')}</th>
            <th className="px-4 py-3 font-semibold text-gray-700 text-center">{tScenario('gcsEvaluationResult.tableTotal')}</th>
            <th className="px-4 py-3 font-semibold text-gray-700 text-center">{tScenario('gcsEvaluationResult.tableResult')}</th>
            <th className="px-4 py-3 font-semibold text-gray-700 text-center">{tScenario('gcsEvaluationResult.tableComment')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row: any, idx: number) => {
            const isExpanded = expandedRow === idx;
            const toggleComment = () => setExpandedRow((prev) => (prev === idx ? null : idx));
            const completed = isScenarioAnswerCorrect(row.completed_missed);
            return (
              <ExpandableCommentTableRows
                key={idx}
                isExpanded={isExpanded}
                toggleComment={toggleComment}
                colSpan={8}
                comment={row.comment}
              >
                <td className="px-4 py-3 text-center text-gray-900">{row.no ?? idx + 1}</td>
                <td className="px-4 py-3 text-start text-gray-900">{row.name ?? '-'}</td>
                <td className="px-4 py-3 text-center text-gray-600">{formatGenderAge(row.gender, row.age)}</td>
                <td className="px-4 py-3 text-center">
                  <GcsComparedValueCell correctVal={row.correct_eye_response ?? row.eye_response} userVal={row.user_eye_response} />
                </td>
                <td className="px-4 py-3 text-center">
                  <GcsComparedValueCell correctVal={row.correct_verbal_response ?? row.verbal_response} userVal={row.user_verbal_response} />
                </td>
                <td className="px-4 py-3 text-center">
                  <GcsComparedValueCell correctVal={row.correct_motor_response ?? row.motor_response} userVal={row.user_motor_response} />
                </td>
                <td className="px-4 py-3 text-center">
                  <GcsComparedValueCell correctVal={row.correct_total ?? row.total} userVal={row.user_total} />
                </td>
                <td className="px-4 py-3 text-center">
                  {row.completed_missed ? (
                    <ScenarioResultBadge
                      ok={completed}
                      labelCorrect={tScenario('gcsEvaluationResult.resultCorrect')}
                      labelWrong={tScenario('gcsEvaluationResult.resultWrong')}
                    />
                  ) : (
                    '-'
                  )}
                </td>
                <td className="px-4 py-3 text-center text-gray-600">
                  <CommentToggleButton
                    isExpanded={isExpanded}
                    onToggle={toggleComment}
                    ariaLabel={isExpanded ? t('simulationResults.collapseComment') : t('simulationResults.expandComment')}
                  />
                </td>
              </ExpandableCommentTableRows>
            );
          })}
        </tbody>
      </table>
    </AssessmentTableShell>
  );
}

interface GcsResultProps {
  title: string;
  resultMetadata: any;
}

/** content_code `gcs` 전용 표 (상단 요약은 SimulationResults `EvalSummaryStatGrid`) */
export default function GcsResult({ title, resultMetadata }: GcsResultProps): React.ReactElement | null {
  return <GcsAssessmentTable title={title} rows={resultMetadata?.gcsPerformance} />;
}
